/**
 * MooringPickupService.ts — finds the closest mooring anchor within
 * boat-hook (bosak) reach of a player-boat cleat.
 *
 * Lives behind a small interface so the rest of the simulation only
 * sees a query: "for cleat X, what (if anything) is in reach?". The
 * UI uses this to enable boat-hook commands ("przełóż przez bojkę")
 * only when an anchor is genuinely catchable from a given cleat, and
 * to cancel an in-flight action if the boat drifts out of range.
 *
 * Policy (which cleats can use a hook, what the reach is, how the
 * suggested rope length is computed) lives ENTIRELY here so it can
 * evolve independently of the orchestrator in `main.ts`.
 */
import type { Boat } from "./Boat";
import type { MooringAnchor } from "./Dock";

/**
 * Maximum reach of a typical telescoping boat hook (bosak), metres.
 * A standard extended bosak gives ~2 m. The crew has to actually
 * thread the line through the buoy eye, but they can lean over the
 * gunwale to do it — so 2 m of fairlead-to-eye 3D distance is the
 * practical limit. Stern cleats sit ~1.3 m off the centreline, so
 * even with the bosak fully extended the boat has to be within
 * ~1 m of the buoy laterally before the eye is catchable.
 */
export const BOAT_HOOK_REACH_M = 2.0;

/** Result of {@link MooringPickupService.findCandidate}. */
export interface MooringPickupCandidate {
  /** Anchor id (matches `AppState.mooringTargets` values). */
  anchorId: string;
  /** Buoy = movable mooring float; bollard = rigid dock fitting. */
  kind: "buoy" | "bollard";
  /** Distance from the cleat fairlead to the anchor, metres. */
  distance: number;
  /** Suggested rope rest length once the line is made fast (metres). */
  restLength: number;
}

/**
 * One station to consider in pickup queries. The orchestrator
 * (main.ts) registers one per player-boat cleat; the service takes
 * a snapshot of fairlead body coords + cleat name, and the boat
 * reference for each query so positions are always live.
 */
export interface CleatStationRef {
  cleatName: string;
  fairleadBody: readonly [number, number, number];
}

/** Per-anchor classification — buoys vs rigid dock fittings. */
export type AnchorKind = "buoy" | "bollard";

export interface ClassifiedAnchor {
  anchor: MooringAnchor;
  kind: AnchorKind;
}

/**
 * Cleats from which a boat hook can plausibly reach. Midship cleats
 * are excluded — you can't lean out beam-on with a hook on most
 * yachts (rigging, lifelines, sheets are in the way).
 */
const PICKUP_ELIGIBLE_CLEATS = new Set<string>([
  "bow",
  "stern_port",
  "stern_starboard",
]);

export class MooringPickupService {
  private readonly stations = new Map<string, CleatStationRef>();
  private readonly anchors = new Map<string, ClassifiedAnchor>();

  constructor(private readonly playerBoat: Boat) {}

  registerStation(station: CleatStationRef): void {
    this.stations.set(station.cleatName, station);
  }

  registerAnchor(anchor: MooringAnchor, kind: AnchorKind): void {
    this.anchors.set(anchor.anchorId, { anchor, kind });
  }

  /** Cleat keys that the UI may query (bow / stern only). */
  pickupEligibleCleats(): readonly string[] {
    return Array.from(PICKUP_ELIGIBLE_CLEATS);
  }

  /** True if the given cleat could ever be used with a boat hook. */
  isPickupEligible(cleatName: string): boolean {
    return PICKUP_ELIGIBLE_CLEATS.has(cleatName);
  }

  /**
   * Closest registered anchor within {@link BOAT_HOOK_REACH_M} of the
   * given cleat's fairlead, or null. Uses 3D distance so a tall
   * bollard high on a dock doesn't count when the boat is alongside
   * but several metres lower.
   */
  findCandidate(cleatName: string): MooringPickupCandidate | null {
    if (!PICKUP_ELIGIBLE_CLEATS.has(cleatName)) return null;
    const station = this.stations.get(cleatName);
    if (!station) return null;

    const [fbX, fbY, fbZ] = station.fairleadBody;
    const [fx, fz] = this.playerBoat.bodyToWorld(fbX, fbY, fbZ);
    const fy = this.playerBoat.posY + fbY;

    let bestId: string | null = null;
    let bestKind: AnchorKind = "buoy";
    let bestDist = BOAT_HOOK_REACH_M;
    for (const [id, { anchor, kind }] of this.anchors) {
      const [ax, ay, az] = anchor.getWorldPos();
      const d = Math.hypot(ax - fx, ay - fy, az - fz);
      if (d <= bestDist) {
        bestDist = d;
        bestId = id;
        bestKind = kind;
      }
    }
    if (!bestId) return null;

    // A little slack so the line settles taut without dunking the buoy
    // on the next swell.
    const restLength = Math.max(1.5, bestDist * 1.2 + 0.5);
    return {
      anchorId: bestId,
      kind: bestKind,
      distance: bestDist,
      restLength,
    };
  }
}
